import React, { useContext } from "react";
import { contextObject } from "./ShoppingCartContext";
import Box from "@mui/material/Box";
import ShoppingCartItem from "../ShoppingCartItem/ShoppingCartItem";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../../navigation/routeConfig";
import { UserContext } from "../../../hooks/userContext";
import { useQueryBankAccount } from "../../../queries/pharmacy/hooks/useQueryBankAccount";
import { CircularProgress, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE = "#E2E8F0";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

interface IShoppingCartProps {
  setCartOpen: (cartOpen: boolean) => void;
}

export const ShoppingCart: React.FunctionComponent<IShoppingCartProps> = ({
  setCartOpen,
}) => {
  const { id: userId }: any = useContext(UserContext);
  const { cart } = useContext(contextObject);
  const navigate = useNavigate();

  const { data, loading } = useQueryBankAccount({
    variables: {
      where: {
        AND: [
          { object: { in: ["payment_method", "card"] } },
          { userId: { equals: +userId } },
          { status: { in: ["VERIFIED", "APPROVED"] } },
          { accountId: { not: { equals: null } } },
        ],
      },
    },
    fetchPolicy: "no-cache",
  });

  const getTotal = () => {
    return cart.reduce((partialSum, a: any) => {
      const price = Number(a.item.price.replace(/[^0-9.-]+/g, ""));
      return partialSum + price * a.quantity;
    }, 0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#FFFFFF",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} size={28} />
      </Box>
    );
  }

  const itemCount = cart.length;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFFFFF",
        fontFamily: BODY_FONT,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${LINE_SOFT}`,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 700,
              fontSize: 20,
              letterSpacing: "-0.01em",
              color: INK,
              lineHeight: 1.25,
            }}
          >
            Cart
          </Box>
          <Box
            sx={{
              fontFamily: MONO_FONT,
              fontSize: "11.5px",
              color: INK_SOFT,
              mt: 0.25,
            }}
          >
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </Box>
        </Box>
        <IconButton
          aria-label="Close cart"
          onClick={() => setCartOpen(false)}
          sx={{ color: INK_SOFT, "&:hover": { bgcolor: LINE_SOFT } }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: "#F8FAFC",
        }}
      >
        {cart.map((cartItem: any) => (
          <ShoppingCartItem cartItem={cartItem} key={cartItem.id} />
        ))}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          borderTop: `1px solid ${LINE}`,
          bgcolor: "#F1F5F9",
          px: 2.5,
          pt: 2,
          pb: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 2,
          }}
        >
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: INK_SOFT,
            }}
          >
            Cart total
          </Box>
          <Box
            sx={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 700,
              fontSize: 22,
              letterSpacing: "-0.02em",
              color: INK,
            }}
          >
            ${getTotal().toFixed(2)}
          </Box>
        </Box>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            setCartOpen(false);
            if (data?.findFirstBankAccount) {
              navigate(routeConfigs.checkout.path);
            } else {
              navigate(`${routeConfigs.bankAccount.path}/?required=true`);
            }
          }}
          sx={{
            bgcolor: PRIMARY,
            color: "#FFFFFF",
            borderRadius: "8px",
            fontWeight: 600,
            fontFamily: BODY_FONT,
            fontSize: 15,
            py: 1.15,
            boxShadow: "none",
            textTransform: "none",
            "&:hover": { bgcolor: PRIMARY_DARK, boxShadow: "none" },
          }}
        >
          Checkout
        </Button>
      </Box>
    </Box>
  );
};

export default ShoppingCart;
